import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { rolesAPI } from '../services/api';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      console.log('Roles API Response:', response);
      console.log('Roles data:', response.data);
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      console.error('Error details:', error.response);
      setRoles([]);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลบทบาทได้', 'error');
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await rolesAPI.getPermissions();
      console.log('Permissions API Response:', response);
      console.log('Permissions data:', response.data);
      setPermissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      console.error('Error details:', error.response);
      setPermissions([]);
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: permissions.map(p => ({
        permission_key: p.key,
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false
      }))
    });
    setShowModal(true);
  };

  const handleEdit = async (role) => {
    try {
      const response = await rolesAPI.getById(role.id);
      const roleData = response.data;

      setEditingRole(roleData);

      // Map permissions with existing values
      const rolePerms = permissions.map(p => {
        const existing = roleData.permissions.find(rp => rp.permission_key === p.key);
        return {
          permission_key: p.key,
          can_view: existing ? existing.can_view : false,
          can_create: existing ? existing.can_create : false,
          can_edit: existing ? existing.can_edit : false,
          can_delete: existing ? existing.can_delete : false
        };
      });

      setFormData({
        name: roleData.name,
        display_name: roleData.display_name,
        description: roleData.description || '',
        permissions: rolePerms
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching role:', error);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลบทบาทได้', 'error');
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system_role) {
      Swal.fire('ไม่สามารถลบได้', 'ไม่สามารถลบบทบาทเริ่มต้นของระบบได้', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'ยืนยันการลบ?',
      text: `คุณต้องการลบบทบาท "${role.display_name}" ใช่หรือไม่?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      try {
        await rolesAPI.delete(role.id);
        Swal.fire('สำเร็จ!', 'ลบบทบาทเรียบร้อยแล้ว', 'success');
        fetchRoles();
      } catch (error) {
        const message = error.response?.data?.error || 'ไม่สามารถลบบทบาทได้';
        Swal.fire('ข้อผิดพลาด', message, 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter permissions to only include those with at least one permission checked
    const activePermissions = formData.permissions.filter(
      p => p.can_view || p.can_create || p.can_edit || p.can_delete
    );

    const submitData = {
      ...formData,
      permissions: activePermissions
    };

    try {
      if (editingRole) {
        await rolesAPI.update(editingRole.id, submitData);
        Swal.fire('สำเร็จ!', 'อัปเดตบทบาทเรียบร้อยแล้ว', 'success');
      } else {
        await rolesAPI.create(submitData);
        Swal.fire('สำเร็จ!', 'เพิ่มบทบาทเรียบร้อยแล้ว', 'success');
      }
      setShowModal(false);
      fetchRoles();
    } catch (error) {
      const message = error.response?.data?.error || 'ไม่สามารถบันทึกข้อมูลได้';
      Swal.fire('ข้อผิดพลาด', message, 'error');
    }
  };

  const handlePermissionChange = (permissionKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.permission_key === permissionKey
          ? { ...p, [field]: value }
          : p
      )
    }));
  };

  const handleSelectAll = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.permission_key === permissionKey
          ? { ...p, can_view: true, can_create: true, can_edit: true, can_delete: true }
          : p
      )
    }));
  };

  const handleDeselectAll = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.permission_key === permissionKey
          ? { ...p, can_view: false, can_create: false, can_edit: false, can_delete: false }
          : p
      )
    }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">จัดการบทบาทและสิทธิ์</h1>
        <button
          onClick={handleCreate}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
        >
          <span>+ เพิ่มบทบาทใหม่</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ชื่อบทบาท
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                คำอธิบาย
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                จำนวนสิทธิ์
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                ประเภท
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                การจัดการ
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {roles.map((role) => (
              <tr key={role.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{role.display_name}</div>
                  <div className="text-sm text-gray-500">{role.name}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {role.description || '-'}
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">
                  {role.permissions_count}
                </td>
                <td className="px-6 py-4 text-center">
                  {role.is_system_role ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      ระบบ
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      กำหนดเอง
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-center text-sm font-medium">
                  <button
                    onClick={() => handleEdit(role)}
                    className="text-primary-600 hover:text-primary-900 mr-3"
                  >
                    แก้ไข
                  </button>
                  {!role.is_system_role && (
                    <button
                      onClick={() => handleDelete(role)}
                      className="text-red-600 hover:text-red-900"
                    >
                      ลบ
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingRole ? 'แก้ไขบทบาท' : 'เพิ่มบทบาทใหม่'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อบทบาท (ภาษาอังกฤษ) *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={editingRole?.is_system_role}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    placeholder="manager"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อแสดง (ภาษาไทย) *
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ผู้จัดการ"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  คำอธิบาย
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="อธิบายบทบาทและความรับผิดชอบ"
                />
              </div>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">กำหนดสิทธิ์การใช้งาน</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          เมนู
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          ดู
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          สร้าง
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          แก้ไข
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          ลบ
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          เลือก
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {permissions.map((permission) => {
                        const rolePermission = formData.permissions.find(
                          p => p.permission_key === permission.key
                        );
                        return (
                          <tr key={permission.key} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{permission.name}</div>
                              <div className="text-xs text-gray-500">{permission.description}</div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={rolePermission?.can_view || false}
                                onChange={(e) => handlePermissionChange(
                                  permission.key,
                                  'can_view',
                                  e.target.checked
                                )}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={rolePermission?.can_create || false}
                                onChange={(e) => handlePermissionChange(
                                  permission.key,
                                  'can_create',
                                  e.target.checked
                                )}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={rolePermission?.can_edit || false}
                                onChange={(e) => handlePermissionChange(
                                  permission.key,
                                  'can_edit',
                                  e.target.checked
                                )}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={rolePermission?.can_delete || false}
                                onChange={(e) => handlePermissionChange(
                                  permission.key,
                                  'can_delete',
                                  e.target.checked
                                )}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => handleSelectAll(permission.key)}
                                className="text-xs text-primary-600 hover:text-primary-900 mr-2"
                              >
                                ทั้งหมด
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeselectAll(permission.key)}
                                className="text-xs text-gray-600 hover:text-gray-900"
                              >
                                ไม่เลือก
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Roles;
